/**
 * Client binding for `generateAiRedFlags` + hook.
 *
 * Same lifecycle as `useAiSynthese` :
 *   - Subscribe to `/users/{uid}/fiches/{id}/ai/redflags` so the UI picks
 *     up server-side writes instantly.
 *   - Fire the callable on mount ; server handles freshness via inputsHash
 *     comparison.
 *
 * One specifity : red flags include the /env/* blocks in their inputs, so
 * opening the AI tab *before* the Environnement tab will produce fewer
 * flags. Once the env hooks populate the subcollection, the callable's
 * hash check will detect the change on next mount and regen silently.
 */

import { useEffect, useState } from "react";
import { doc, onSnapshot, type DocumentData } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, auth, functions } from "../firebase";

// ─── Doc shape (keep in sync with functions/src/generateAiRedFlags.ts) ──
export type AiRedFlagSeverity = "info" | "warn" | "critical";

export type AiRedFlag = {
  severity: AiRedFlagSeverity;
  title: string;
  detail: string;
  sourceRef: string | null;
};

export type AiRedFlags = {
  version: 1;
  flags: AiRedFlag[];
  generatedAt: string;
  model: string;
  inputsHash: string;
  pdfInclude: boolean;
};

const callGenerate = httpsCallable<{ ficheId: string }, AiRedFlags>(
  functions,
  "generateAiRedFlags",
);

// ─── Hook ────────────────────────────────────────────────────────────────
type State =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ready"; data: AiRedFlags; fromServer: boolean }
  | { status: "error"; message: string };

export function useAiRedFlags(ficheId: string | null): State {
  const [state, setState] = useState<State>({ status: "idle" });

  useEffect(() => {
    if (!ficheId) {
      setState({ status: "idle" });
      return;
    }
    const user = auth.currentUser;
    if (!user) {
      setState({ status: "idle" });
      return;
    }

    let cancelled = false;
    setState({ status: "loading" });

    const ref = doc(
      db,
      "users",
      user.uid,
      "fiches",
      ficheId,
      "ai",
      "redflags",
    );
    const unsubscribe = onSnapshot(
      ref,
      (snap) => {
        if (cancelled) return;
        if (!snap.exists()) return;
        const parsed = parseAiRedFlags(snap.data());
        if (parsed) {
          setState({ status: "ready", data: parsed, fromServer: false });
        }
      },
      (err) => {
        console.warn("[ai/redflags] snapshot error", err);
      },
    );

    callGenerate({ ficheId })
      .then((res) => {
        if (cancelled) return;
        setState({
          status: "ready",
          data: res.data as AiRedFlags,
          fromServer: true,
        });
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setState((prev) =>
          prev.status === "ready"
            ? prev
            : { status: "error", message: extractMsg(e) },
        );
      });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [ficheId]);

  return state;
}

// ─── Helpers ─────────────────────────────────────────────────────────────
function parseAiRedFlags(raw: DocumentData): AiRedFlags | null {
  if (!raw || raw.version !== 1) return null;
  const flagsRaw = Array.isArray(raw.flags) ? raw.flags : null;
  if (!flagsRaw) return null;
  const flags: AiRedFlag[] = [];
  for (const f of flagsRaw) {
    if (!f || typeof f !== "object") continue;
    const sev = f.severity;
    if (sev !== "info" && sev !== "warn" && sev !== "critical") continue;
    const title = typeof f.title === "string" ? f.title : null;
    const detail = typeof f.detail === "string" ? f.detail : null;
    if (!title || !detail) continue;
    flags.push({
      severity: sev,
      title,
      detail,
      sourceRef: typeof f.sourceRef === "string" ? f.sourceRef : null,
    });
  }
  return {
    version: 1,
    flags,
    generatedAt: typeof raw.generatedAt === "string" ? raw.generatedAt : "",
    model: typeof raw.model === "string" ? raw.model : "",
    inputsHash: typeof raw.inputsHash === "string" ? raw.inputsHash : "",
    pdfInclude: Boolean(raw.pdfInclude),
  };
}

function extractMsg(e: unknown): string {
  if (e instanceof Error) return e.message;
  if (typeof e === "object" && e && "message" in e) {
    const m = (e as { message?: unknown }).message;
    if (typeof m === "string") return m;
  }
  return String(e);
}
